import React, { useEffect, useState, Suspense, lazy } from "react";
import { useSelector } from "react-redux";
import { useLocation } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import { useTranslation } from 'react-i18next';
import SwitchBtn from "../components/SwitchBtn/Switch";
import HomeHead from "../components/HomeHead";
import HomeBody from "../components/HomeBody/HomeBody";
import HomeBodyActor from "../components/HomeBody/HomeBodyActor";
import FilterTag from "../components/Filter/FilterTag";
import { FepkContext } from "../context/FepkContext";
import { getHomeViewType } from "../utils/homeViewType";
import http from "../http-common";

import LandingHero from "../components/LandingPage/LandingHero";
import LandingIntroductions from "../components/LandingPage/LandingIntroductions";
import withChunkReload from "../utils/withChunkReload";

const LandingAcademy = lazy(() => withChunkReload(() => import("../components/LandingPage/LandingAcademy")));
const LandingFilmCarousels = lazy(() => withChunkReload(() => import("../components/LandingPage/LandingFilmCarousels")));
const LandingInstructions = lazy(() => withChunkReload(() => import("../components/LandingPage/LandingInstructions")));
const LandingFaq = lazy(() => withChunkReload(() => import("../components/LandingPage/LandingFaq")));
const LandingNewsletter = lazy(() => withChunkReload(() => import("../components/LandingPage/LandingNewsletter")));

function Home() {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const location = useLocation();
  const viewType = getHomeViewType(location.pathname);
  const user = useSelector((state) => state.user);
  usePageTitle(viewType === 'actor' ? 'Actors' : viewType === 'filmmaker' ? 'Filmmakers' : null);
  const {setFepkMaker, setFilterQuery} = React.useContext(FepkContext);

  useEffect(() => {
    setFepkMaker([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setFilterQuery([]);
  }, [location.pathname, setFilterQuery]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const fetchData = async () => {
      try {
        const endpoint =
          viewType === 'actor'
            ? 'users/getallactors/'
            : viewType === 'filmmaker'
            ? 'users/getallfilmmakers/'
            : 'fepks/';

        const response = await http.get(endpoint);
        if (!cancelled) setData(response.data);
      } catch (error) {
        if (!cancelled) console.error("Error fetching data:", error);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [viewType, user]);

  return (
    <>
      <div className='tw-w-full'>
        {user && (
          <>
            <HomeHead role={viewType} data={data} />
            <SwitchBtn />
            {viewType === 'actor' ? (
              <>
                <FilterTag isActive="actor" />
                <HomeBodyActor data={data} />
              </>
            ) : viewType === 'filmmaker' ? (
              <>
                <FilterTag isActive="filmmaker" />
                <HomeBodyActor data={data} roleLabel={t('Filmmakers')} emptyLabel={t('No filmmakers match your filters.')} showRepresented={false} />
              </>
            ) : (
              <HomeBody data={data} />
            )}
          </>
        )}
        {!user && (
          <main>
            <LandingHero />
            <LandingIntroductions />

            <Suspense fallback={<div className="tw-min-h-[50vh] tw-w-full tw-bg-midnight"></div>}>
              <LandingAcademy />
              <LandingFilmCarousels />
              <LandingInstructions />
              <LandingFaq />
              <LandingNewsletter />
            </Suspense>
          </main>
        )}
      </div>
    </>
  );
}

export default Home;